import { WebSocketLink } from '@apollo/link-ws';
import { UUIDOperationIdSubscriptionClient } from './UUIDOperationIdSubscriptionClient';
import { createAppSyncAuthorizedWebSocket } from './appSyncAuthorizedWebSocket';
import { cacheWithAsyncRefresh } from './asyncUtils';
import { createAppSyncGraphQLOperationAdapter } from './appSyncGraphQLOperationAdapter';

const APPSYNC_MAX_CONNECTION_TIMEOUT_MILLISECONDS = 5 * 60 * 1000;

export const createAppSyncSubscriptionWebsocketLink = async ({
	appSyncApiUrl,
	appSyncRealtimeUrl,
	getJwtToken
}) => {
	// Realtime endpoint resolution :
	//
	// 1. Preferred — `appSyncRealtimeUrl` is set explicitly (modern config).
	//    The connection opens against the realtime endpoint, but the
	//    sub-protocol auth `host` field must identify the API via its
	//    HTTP host (`...appsync-api...`) — AppSync rejects with
	//    `HttpNotFoundException` if we send the realtime host there.
	//    Derive the HTTP host by swapping `-realtime-api` back to `-api`.
	//
	// 2. Fallback — derive from `appSyncApiUrl`. Handles the native AppSync
	//    URL (host swap `appsync-api` → `appsync-realtime-api`) and the
	//    legacy CloudFront-proxied `/graphql/realtime` pattern. The latter
	//    is broken since the CloudFront realtime proxy was removed (see
	//    incident INC-2026-001) — kept only for backwards compatibility
	//    with older config blobs that have not yet been redeployed.
	const appSyncApiHost = new URL(appSyncApiUrl).host;
	let wsUrl;
	let wsHost;
	if (appSyncRealtimeUrl) {
		wsUrl = appSyncRealtimeUrl;
		wsHost = new URL(appSyncRealtimeUrl).host.replace(
			'appsync-realtime-api',
			'appsync-api'
		);
	} else {
		const isCustomDomain = !appSyncApiHost.includes('appsync-api');
		wsUrl = isCustomDomain
			? `wss://${appSyncApiHost}/graphql/realtime`
			: `wss://${appSyncApiHost.replace('appsync-api', 'appsync-realtime-api')}/graphql`;
		wsHost = appSyncApiHost;
	}

	const getAppSyncAuthorizationInfo = async () => ({
		host: wsHost,
		Authorization: await getJwtToken()
	});

	return new WebSocketLink(
		new UUIDOperationIdSubscriptionClient(
			wsUrl,
			{
				timeout: APPSYNC_MAX_CONNECTION_TIMEOUT_MILLISECONDS,
				reconnect: true,
				lazy: true
			},
			// We want to avoid expired authorization information being used but SubscriptionClient synchronously
			// instantiates websockets (on connection/reconnection) so the best we can do is schedule an async refresh
			// and suffer failed connection attempts until a fresh token has been retrieved
			createAppSyncAuthorizedWebSocket(
				await cacheWithAsyncRefresh(getAppSyncAuthorizationInfo)
			)
		).use([
			createAppSyncGraphQLOperationAdapter(getAppSyncAuthorizationInfo)
		])
	);
};
